/**
 * The Code tab of a saved page on web (plan 7.3): CodeMirror with the HTML
 * language, editable on the latest version (Save, or Cmd or Ctrl+S, sends
 * `PUT page-source`; the server sanitizes and checks it like a new page and
 * says what it removed: "Scripts, forms and external files were removed.")
 * and read only on an older version or without editing. The code is the
 * page from `editor-source` without the editor's additions (`page-code.ts`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PageEditsResult } from '@tabbio/types';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { getEditorSource, savePageSource } from '../../../lib/artifact-edits-api';
import { useInvalidateArtifact } from '../../../hooks/useArtifactVersions';
import { sendArtifactQuickAction } from '../../../lib/artifact-ask';
import type { EditableCodeView, ReadOnlyCodeView } from '../artifact-codemirror.web';
import { notePageSave } from '../suggestions/suggestions-store';
import { FindingsBanner } from './EditorChrome.web';
import { createUuid } from './editor-ids';
import { pageCodeFromEditorSource, pageSourceForSave, type PageCode } from './page-code';
import { classifySaveError } from './save-errors';
import { saveStateForFailure, type EditorSaveState } from './save-state';

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);
}

export function PageCodeEditor({
  artifactId,
  title,
  threadId,
  versionId,
  editable,
  onSaveState,
  onAsked,
}: {
  artifactId: string;
  title: string;
  threadId: string | null;
  /** An older version (read only); null is the head. */
  versionId: string | null;
  editable: boolean;
  onSaveState?: (state: EditorSaveState) => void;
  onAsked?: () => void;
}) {
  const { t } = useTranslation('common');
  const invalidate = useInvalidateArtifact();
  const host = useRef<HTMLDivElement | null>(null);
  const [source, setSource] = useState<{ versionId: string; code: PageCode } | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [state, setState] = useState<EditorSaveState>({ status: 'idle' });
  const [result, setResult] = useState<PageEditsResult | null>(null);
  const [ready, setReady] = useState(false);
  const base = useRef<string | null>(null);
  const editSessionId = useMemo(() => createUuid(), []);
  const view = useRef<EditableCodeView | ReadOnlyCodeView | null>(null);
  const value = useRef('');
  const saving = useRef(false);
  const canEdit = editable && !versionId;

  const load = useCallback(async () => {
    setLoadFailed(false);
    setSource(null);
    try {
      const answer = await getEditorSource(artifactId, versionId);
      const code = pageCodeFromEditorSource(answer.html);
      base.current = answer.versionId;
      value.current = code.code;
      setSource({ versionId: answer.versionId, code });
      setDirty(false);
    } catch {
      setLoadFailed(true);
    }
  }, [artifactId, versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSaveStateRef = useRef(onSaveState);
  onSaveStateRef.current = onSaveState;
  const setSaveState = useCallback((next: EditorSaveState) => {
    setState(next);
    onSaveStateRef.current?.(next);
  }, []);

  const save = useCallback(async () => {
    if (!canEdit || !source || saving.current || !base.current) return;
    saving.current = true;
    setSaveState({ status: 'saving' });
    const baseVersionId = base.current;
    try {
      const answer = await savePageSource(artifactId, {
        baseVersionId,
        editSessionId,
        html: pageSourceForSave(value.current, source.code.images),
      });
      base.current = answer.version.id;
      notePageSave(artifactId, baseVersionId, answer.version.id, undefined);
      setResult(answer);
      setDirty(false);
      setSaveState({ status: 'saved' });
      invalidate(artifactId);
    } catch (error) {
      setSaveState(saveStateForFailure(classifySaveError(error)));
    } finally {
      saving.current = false;
    }
  }, [artifactId, canEdit, editSessionId, invalidate, setSaveState, source]);
  const saveRef = useRef(save);
  saveRef.current = save;

  // The editor chunk loads once per source.
  useEffect(() => {
    if (!source || !host.current) return;
    let alive = true;
    let mounted: EditableCodeView | ReadOnlyCodeView | null = null;
    setReady(false);
    const label = canEdit
      ? t('chat.artifacts.edit.codeEditLabel', { title })
      : t('chat.artifacts.building.codeLabel', { title });
    void import('../artifact-codemirror.web').then((codemirror) => {
      if (!alive || !host.current) return;
      mounted = canEdit
        ? codemirror.createEditableHtmlView(host.current, source.code.code, {
            label,
            onChange: (next) => {
              value.current = next;
              setDirty(true);
            },
            onSave: () => void saveRef.current(),
          })
        : codemirror.createReadOnlyHtmlView(host.current, source.code.code, { label, onScroll: () => {} });
      view.current = mounted;
      setReady(true);
    });
    return () => {
      alive = false;
      mounted?.destroy();
      view.current = null;
    };
    // The label only matters at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, canEdit]);

  const shortcut = isMac() ? 'Cmd+S' : 'Ctrl+S';
  const errorWords =
    state.status === 'invalid' || state.status === 'limited'
      ? state.message ?? t('chat.artifacts.edit.saveFailed')
      : state.status === 'failed'
        ? t('chat.artifacts.edit.saveFailed')
        : state.status === 'conflict'
          ? t('chat.artifacts.edit.conflict')
          : null;

  return (
    <div className="tabbio-code-editor">
      {canEdit ? (
        <div className="tabbio-code-editor-bar">
          <span className="tabbio-code-editor-status" role="status" aria-live="polite">
            {state.status === 'saving' ? (
              <>
                <SpinnerGlyph color="var(--text-secondary)" size={14} />
                {t('chat.artifacts.edit.codeSaving')}
              </>
            ) : errorWords ? (
              <span className="is-error">{errorWords}</span>
            ) : dirty ? (
              t('chat.artifacts.edit.codeUnsaved')
            ) : state.status === 'saved' ? (
              <>
                <MaterialSymbol name="cloud_done" size={16} />
                {t('chat.artifacts.edit.codeSaved')}
              </>
            ) : null}
          </span>
          {state.status === 'conflict' ? (
            <button
              type="button"
              className="tabbio-text-button"
              onClick={() => {
                setSaveState({ status: 'idle' });
                void load();
              }}
            >
              {t('chat.artifacts.edit.reload')}
            </button>
          ) : null}
          <button
            type="button"
            className="tabbio-panel-primary is-small"
            onClick={() => void save()}
            disabled={!dirty || state.status === 'saving' || state.status === 'conflict'}
            aria-label={t('chat.artifacts.edit.codeSaveLabel', { shortcut })}
            title={t('chat.artifacts.edit.codeSaveLabel', { shortcut })}
            aria-keyshortcuts={isMac() ? 'Meta+S' : 'Control+S'}
          >
            {t('chat.artifacts.edit.codeSave')}
          </button>
        </div>
      ) : null}
      {result ? (
        <FindingsBanner
          findings={result.findings ?? []}
          warnings={result.warnings ?? []}
          errorCount={result.findings?.length ?? 0}
          warningCount={result.warnings?.length ?? 0}
          removed={result.removed}
          onFix={() => {
            sendArtifactQuickAction({ text: t('chat.artifacts.edit.fixPrompt'), context: { artifactId }, threadId });
            onAsked?.();
          }}
        />
      ) : null}
      <div className="tabbio-draft-code">
        <div ref={host} className="tabbio-draft-code-host" dir="ltr" />
        {loadFailed ? (
          <div className="tabbio-artifact-panel-message">
            <span className="tabbio-artifact-panel-message-title">{t('chat.artifacts.edit.loadFailed')}</span>
            <button type="button" className="tabbio-panel-primary" onClick={() => void load()}>
              {t('chat.artifacts.edit.retry')}
            </button>
          </div>
        ) : !ready ? (
          <div className="tabbio-draft-code-loading" aria-hidden>
            <SpinnerGlyph color="var(--text-secondary)" size={20} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
